import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, Gauge, Loader2 } from 'lucide-react';
import api from '../utils/axios';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const mesCorto = (m) => `${MESES[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;
const pct = (t) => `${Math.round((t || 0) * 100)}%`;
const tonoTasa = (t, g) => (!g ? 'text-slate-300' : t > 0.9 ? 'text-red-600 font-bold' : t > 0.6 ? 'text-amber-600 font-semibold' : 'text-emerald-700');

const Motivos = ({ porMotivo, motivos }) => {
  const e = Object.entries(porMotivo || {});
  if (!e.length) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {e.sort((a, b) => b[1] - a[1]).map(([k, n]) => (
        <span key={k} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{motivos[k] || k}: {n}</span>
      ))}
    </div>
  );
};

const FilaMonitor = ({ m, motivos, abierta, onToggle }) => (
  <>
    <tr onClick={onToggle} data-testid={`calidad-fila-${m.monitor_id || m.nombre}`}
      className={`cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50 ${m.alerta_descarte ? 'bg-red-50/60' : ''}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${abierta ? 'rotate-180' : ''}`} />
          <span className="font-medium text-slate-800">{m.nombre}</span>
          {m.alerta_descarte && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="calidad-alerta-descarte">
              <AlertTriangle size={11} /> +90% descartado (3 meses)
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{m.total.generados}</td>
      <td className="px-4 py-3 text-right tabular-nums">{m.total.descartados}</td>
      <td className={`px-4 py-3 text-right tabular-nums ${tonoTasa(m.total.tasa_descarte, m.total.generados)}`}>{pct(m.total.tasa_descarte)}</td>
      <td className={`px-4 py-3 text-right tabular-nums ${tonoTasa(m.ultimos_3m.tasa_descarte, m.ultimos_3m.generados)}`}>
        {m.ultimos_3m.generados ? `${pct(m.ultimos_3m.tasa_descarte)} (${m.ultimos_3m.descartados}/${m.ultimos_3m.generados})` : '—'}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{m.total.con_ros}</td>
      <td className="px-4 py-3 text-right tabular-nums">{m.total.con_bloqueo}</td>
      <td className="px-4 py-3"><Motivos porMotivo={m.total.por_motivo} motivos={motivos} /></td>
    </tr>
    {abierta && (
      <tr className="bg-slate-50/70" data-testid={`calidad-detalle-${m.monitor_id || m.nombre}`}>
        <td colSpan={8} className="px-4 pb-4 pt-1">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
              <th className="py-1.5">Mes</th><th className="py-1.5 text-right">Generados</th><th className="py-1.5 text-right">Descartados</th>
              <th className="py-1.5 text-right">Tasa</th><th className="py-1.5 text-right">Con ROS</th><th className="py-1.5 text-right">Con bloqueo</th>
              <th className="py-1.5 pl-4">Motivos de descarte</th>
            </tr></thead>
            <tbody>
              {m.por_mes.map((g) => (
                <tr key={g.mes} className="border-t border-slate-200/70">
                  <td className="py-1.5 font-mono">{mesCorto(g.mes)}</td>
                  <td className="py-1.5 text-right tabular-nums">{g.generados || '·'}</td>
                  <td className="py-1.5 text-right tabular-nums">{g.descartados || '·'}</td>
                  <td className={`py-1.5 text-right tabular-nums ${tonoTasa(g.tasa_descarte, g.generados)}`}>{g.generados ? pct(g.tasa_descarte) : '·'}</td>
                  <td className="py-1.5 text-right tabular-nums">{g.con_ros || '·'}</td>
                  <td className="py-1.5 text-right tabular-nums">{g.con_bloqueo || '·'}</td>
                  <td className="py-1.5 pl-4"><Motivos porMotivo={g.por_motivo} motivos={motivos} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    )}
  </>
);

// J1: calidad de monitores por monitor y por mes (solo lectura).
export default function CalidadMonitores() {
  const [meses, setMeses] = useState('12');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [abierta, setAbierta] = useState(null);

  useEffect(() => {
    let vivo = true;
    setData(null);
    setError(null);
    api.get('/calidad-monitores', { params: { meses }, timeout: 120000 })
      .then((r) => vivo && setData(r.data))
      .catch((e) => vivo && setError(e.response?.data?.detail || 'No se pudo cargar el tablero'));
    return () => { vivo = false; };
  }, [meses]);

  const alertas = data?.monitores.filter((m) => m.alerta_descarte).length || 0;
  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-8" data-testid="calidad-monitores-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Gauge className="text-sky-600" /> Calidad de monitores</h1>
          <p className="mt-1 text-sm text-slate-500">Episodios generados, descartados (por motivo), con ROS y con bloqueo, por monitor y por mes.</p>
        </div>
        <Select value={meses} onValueChange={setMeses}>
          <SelectTrigger className="w-44 bg-white" data-testid="calidad-meses"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="3">Últimos 3 meses</SelectItem>
            <SelectItem value="6">Últimos 6 meses</SelectItem>
            <SelectItem value="12">Últimos 12 meses</SelectItem>
            <SelectItem value="24">Últimos 24 meses</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {data && alertas > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="calidad-resumen-alerta">
          <AlertTriangle size={16} /> {alertas} monitor(es) con más de 90% de descarte en los últimos 3 meses.
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {error ? <p className="py-10 text-center text-sm text-red-500" data-testid="calidad-error">{error}</p>
          : !data ? <div className="flex justify-center py-14"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
            : data.monitores.length === 0 ? <p className="py-10 text-center text-sm text-slate-400" data-testid="calidad-vacio">No hay episodios en el período.</p>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="calidad-tabla">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3 font-medium">Monitor</th><th className="px-4 py-3 text-right font-medium">Generados</th>
                        <th className="px-4 py-3 text-right font-medium">Descartados</th><th className="px-4 py-3 text-right font-medium">Tasa de descarte</th>
                        <th className="px-4 py-3 text-right font-medium">Últimos 3 meses</th><th className="px-4 py-3 text-right font-medium">Con ROS</th>
                        <th className="px-4 py-3 text-right font-medium">Con bloqueo</th><th className="px-4 py-3 font-medium">Motivos de descarte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.monitores.map((m) => {
                        const k = m.monitor_id || m.nombre;
                        return <FilaMonitor key={k} m={m} motivos={data.motivos} abierta={abierta === k} onToggle={() => setAbierta(abierta === k ? null : k)} />;
                      })}
                    </tbody>
                  </table>
                </div>
              )}
      </div>
      <p className="mt-3 text-xs text-slate-400">
        Con ROS / con bloqueo: episodios no descartados cuyo caso tiene un ROS (generado o presentado) o la cuenta bloqueada.
        Los episodios marcados como falso positivo antes de esta versión cuentan como descartados.
      </p>
    </div>
  );
}
